import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  Dialog,
  Tabs,
  Tab,
  IconButton,
  Button,
} from '@mui/material';
import {
  getTraineesByTrainerId,
  getGoal,
  getMeal,
  getProgram,
  getWeightProgress,
  getNutritionPlans,
  getImage
} from '../../utils/api';
import { Pie, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { InsertChart, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import Loader from '../Loader';
import Modal from 'react-modal';
import * as pdfjsLib from 'pdfjs-dist/webpack';

// Set the app element for react-modal accessibility
Modal.setAppElement('#root');

const Portal = ({ userId }) => {
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [timePeriod, setTimePeriod] = useState('daily');
  const [calorieChartData, setCalorieChartData] = useState(null);

  // States for thumbnails and PDF modal
  const [nutritionThumbnails, setNutritionThumbnails] = useState([]);
  const [programThumbnails, setProgramThumbnails] = useState([]);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfScale, setPdfScale] = useState(0.6);

  const mealTypes = {
    breakfast: "ארוחת בוקר",
    lunch: "ארוחת צהריים",
    dinner: "ארוחת ערב",
    snack: "נשנושים",
  };

  const arrayToBase64 = (array) => {
    const uint8Array = new Uint8Array(array);
    const binaryString = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
    return btoa(binaryString);
  };

  useEffect(() => {
    const fetchTraineesData = async () => {
      try {
        setLoading(true);
        const traineesList = await getTraineesByTrainerId(userId);
        if (!traineesList || !Array.isArray(traineesList)) {
          throw new Error('Invalid trainees data');
        }

        const traineesData = await Promise.all(
          traineesList.map(async (trainee) => {
            try {
              const goal = await getGoal(trainee._id).catch(() => null);
              const meals = await getMeal(trainee._id).catch(() => []);
              const programs = await getProgram(trainee._id).catch(() => []);
              const nutritionPlans = await getNutritionPlans(trainee._id).catch(() => []);
              const weightProgress = await getWeightProgress(trainee._id).catch(() => []);
              const profileImageBase = await getImage(trainee._id);

              const today = new Date().setHours(0, 0, 0, 0);
              const todayMeals = Array.isArray(meals)
                ? meals.filter((meal) => new Date(meal.date).setHours(0, 0, 0, 0) === today)
                : [];
              const dailyCalories = todayMeals.reduce(
                (sum, meal) => sum + (meal.foodNutrients?.find((n) => n.nutrientName === 'Energy')?.value || 0),
                0
              );

              const mealTypeCalories = todayMeals.reduce((acc, meal) => {
                acc[meal.mealType] = (acc[meal.mealType] || 0) + (meal.foodNutrients?.find((n) => n.nutrientName === 'Energy')?.value || 0);
                return acc;
              }, {});

              const profileImageBase64 = profileImageBase?.image;

              return {
                ...trainee,
                profileImageBase64,
                caloricGoal: goal?.[0]?.calories || 'N/A',
                dailyCalories,
                programs: Array.isArray(programs) ? programs : [],
                nutritionPlans: Array.isArray(nutritionPlans) ? nutritionPlans : [],
                weightProgress: Array.isArray(weightProgress) ? weightProgress : [],
                mealTypeCalories,
                todayMeals,
                allMeals: meals,
              };
            } catch (err) {
              console.error(`Error processing data for trainee ${trainee._id}:`, err);
              return {
                ...trainee,
                profileImageBase64: '/default-avatar.png',
                caloricGoal: 'N/A',
                dailyCalories: 0,
                programs: [],
                nutritionPlans: [],
                weightProgress: [],
                mealTypeCalories: {},
                todayMeals: [],
                allMeals: [],
              };
            }
          })
        );

        setTrainees(traineesData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load trainees data: ' + err.message);
        setLoading(false);
        console.error(err);
      }
    };

    fetchTraineesData();
  }, [userId]);

  const handleOpenChart = (trainee) => {
    setSelectedTrainee(trainee);
    setChartOpen(true);
    setTabIndex(0);
    setTimePeriod('daily');
  };

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };
  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'plan.pdf';
    link.click();
  };
  // Helper functions for PDFs
  const getBlobFromFileBlob = (fileBlob) => {
    // Case 1: Already a Blob
    if (fileBlob instanceof Blob) {
      return fileBlob;
    }
    // Case 2: Base64 string
    if (typeof fileBlob === "string") {
      const byteCharacters = atob(fileBlob);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      return new Blob(byteArrays, { type: "application/pdf" });
    }
    // Case 3: Buffer-like object (e.g., { type: 'Buffer', data: [...] })
    if (fileBlob && fileBlob.type === "Buffer" && Array.isArray(fileBlob.data)) {
      const bytes = new Uint8Array(fileBlob.data);
      return new Blob([bytes], { type: "application/pdf" });
    }
    // Fallback: Log error and return null for invalid types
    console.error("Invalid fileBlob type:", fileBlob);
    return null;
  };

    const generateThumbnail = async fileBlob => {
        const blob = getBlobFromFileBlob(fileBlob);
        if (!blob) return null;
        const url = URL.createObjectURL(blob) ;
        try {
            const doc = await pdfjsLib.getDocument(url).promise;
            const page = await doc.getPage(1);
            const viewport = page.getViewport({ scale: 0.5 });
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: ctx, viewport }).promise;
            return canvas.toDataURL();
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            URL.revokeObjectURL(url);
        }
    };
  const openPdfModal = (fileBlob) => {
    const blob = getBlobFromFileBlob(fileBlob);
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);
    setPdfModalOpen(true);
  };

  const closePdfModal = () => {
    setPdfModalOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const renderPDF = async (pdfDoc) => {
    const canvasContainer = document.getElementById("pdf-container");
    if (!canvasContainer) return;
    canvasContainer.innerHTML = "";
    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const viewport = page.getViewport({ scale: pdfScale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;
      canvasContainer.appendChild(canvas);
    }
  };

  // Generate thumbnails when a trainee is selected
  useEffect(() => {
    if (selectedTrainee) {
      const generateThumbnails = async () => {
        const nutritionThumbnailPromises = selectedTrainee.nutritionPlans.map(async (plan) => {
          const thumbnailUrl = await generateThumbnail(plan.fileBlob);
          return { id: plan._id, thumbnailUrl };
        });
        const programThumbnailPromises = selectedTrainee.programs.map(async (program) => {
          const thumbnailUrl = await generateThumbnail(program.fileBlob);
          return { id: program._id, thumbnailUrl };
        });
        const [nutritionThumbnailsData, programThumbnailsData] = await Promise.all([
          Promise.all(nutritionThumbnailPromises),
          Promise.all(programThumbnailPromises),
        ]);
        setNutritionThumbnails(nutritionThumbnailsData);
        setProgramThumbnails(programThumbnailsData);
      };
      generateThumbnails();
    } else {
      setNutritionThumbnails([]);
      setProgramThumbnails([]);
    }
  }, [selectedTrainee]);

  // Load and render PDF when modal opens
  useEffect(() => {
    if (pdfUrl && pdfModalOpen) {
      pdfjsLib.getDocument(pdfUrl).promise
        .then((doc) => {
          setPdfDoc(doc);
          renderPDF(doc);
        })
        .catch((error) => {
          console.error("Error loading PDF document:", error);
        });
    }
  }, [pdfUrl, pdfModalOpen]);

  // Re-render PDF when scale changes
  useEffect(() => {
    if (pdfDoc) {
      renderPDF(pdfDoc);
    }
  }, [pdfScale]);

  const getCalorieChartData = (meals, period) => {
    const calorieData = {};

    meals.forEach((meal) => {
      const date = new Date(meal.date);
      const calories = meal.foodNutrients?.find((n) => n.nutrientName === 'Energy')?.value || 0;

      let key;
      switch (period) {
        case 'weekly':
          const week = `${date.getFullYear()}-W${Math.ceil((date.getDate() + 1 - date.getDay()) / 7)}`;
          key = week;
          break;
        case 'monthly':
          key = `${date.getFullYear()}-${date.getMonth() + 1}`;
          break;
        case 'yearly':
          key = `${date.getFullYear()}`;
          break;
        default:
          key = date.toLocaleDateString();
      }

      calorieData[key] = (calorieData[key] || 0) + calories;
    });

    const sortedKeys = Object.keys(calorieData).sort((a, b) => {
      if (period === 'daily') {
        return new Date(a) - new Date(b);
      } else if (period === 'weekly') {
        const [yearA, weekA] = a.split('-W');
        const [yearB, weekB] = b.split('-W');
        return parseInt(yearA) - parseInt(yearB) || parseInt(weekA) - parseInt(weekB);
      } else if (period === 'monthly') {
        const [yearA, monthA] = a.split('-');
        const [yearB, monthB] = b.split('-');
        return parseInt(yearA) - parseInt(yearB) || parseInt(monthA) - parseInt(monthB);
      } else if (period === 'yearly') {
        return parseInt(a) - parseInt(b);
      }
    });

    return {
      labels: sortedKeys,
      datasets: [
        {
          label: 'סך הקלוריות',
          data: sortedKeys.map((key) => calorieData[key]),
          backgroundColor: '#C4A484',
          borderColor: '#C4A484',
          borderWidth: 1,
          fill: true,
        },
      ],
    };
  };

  useEffect(() => {
    if (selectedTrainee && selectedTrainee.allMeals) {
      const data = getCalorieChartData(selectedTrainee.allMeals, timePeriod);
      setCalorieChartData(data);
    }
  }, [selectedTrainee, timePeriod]);

  const renderTraineeMealDetails = () => {
    if (!selectedTrainee.todayMeals || selectedTrainee.todayMeals.length === 0) {
      return <Typography direction="rtl" textAlign="right">אין ארוחות שנרשמו להיום.</Typography>;
    }

    const groupedMeals = selectedTrainee.todayMeals.reduce((acc, meal) => {
      acc[meal.mealType] = acc[meal.mealType] || [];
      acc[meal.mealType].push(meal);
      return acc;
    }, {});

    return (
      <Box>
        {Object.entries(groupedMeals).map(([mealType, meals]) => (
          <Box key={mealType} mb={3}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'GveretLevin',
                direction: 'rtl',
                textAlign: 'right',
                color: '#B5A78A',
              }}
            >
              {mealTypes[mealType] || mealType}
            </Typography>
            {meals.map((meal, index) => (
              <Tooltip
                key={index}
                title={
                  <Box>
                    {meal.foodNutrients
                      ?.filter((nutrient) => nutrient.value > 0)
                      .map((nutrient) => (
                        <Typography key={nutrient.nutrientId} variant="body2">
                          {nutrient.nutrientName}: {nutrient.value} {nutrient.unitName}
                        </Typography>
                      ))}
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    boxShadow: 2,
                    borderRadius: 2,
                    backgroundColor: 'white',
                    direction: 'rtl',
                    textAlign: 'right',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'GveretLevin',
                      color: '#B5A78A',
                    }}
                  >
                    {meal.description}
                  </Typography>
                  <Typography variant="body2">
                    קלוריות: {meal.foodNutrients?.find((n) => n.nutrientName === 'Energy')?.value || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    משקל: {meal.weight || 'N/A'} גרם
                  </Typography>
                  <Tooltip
                    title={meal.ingredients || "אין מרכיבים זמינים"}
                    arrow
                    placement="top"
                  >
                    <Typography variant="body2">
                      מרכיבים: {meal.ingredients ? (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'primary.main',
                          }}
                        >
                          הצג
                        </Typography>
                      ) : (
                        'N/A'
                      )}
                    </Typography>
                  </Tooltip>
                </Box>
              </Tooltip>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) return <Loader />;
  if (error) return <Typography color="error">{error}</Typography>;

  const activeStyle = {
    cursor: 'pointer',
    fontWeight: 'bold',
    color: '#d5011e',
  };

  const inactiveStyle = {
    cursor: 'pointer',
    color: '#333',
    textDecoration: 'none',
  };

  return (
    <Box sx={{ mt: { lg: '96px', xs: '60px' }, px: { lg: '20px', xs: '10px' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontFamily: 'GveretLevin', color: '#B5A78A' }}>
          פורטל מאמנים
        </Typography>
      </Box>

      {trainees.length === 0 ? (
        <Typography justifyContent="center" alignItems="center" direction="rtl">לא נמצאו מתאמנים</Typography>
      ) : (
        trainees.map((trainee) => (
          <Box
            key={trainee._id}
            sx={{
              mb: 2,
              p: 2,
              boxShadow: 2,
              borderRadius: 2,
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={trainee.profileImageBase64}
                alt={trainee.username}
                style={{ width: '50px', height: '50px', borderRadius: '50%', marginRight: '15px' }}
              />
              <Box sx={{ direction: 'rtl', textAlign: 'right' }}>
                <Typography variant="h6" sx={{ fontFamily: 'GveretLevin', color: '#C4A484' }}>
                  {trainee.username}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  טלפון: {trainee.phone || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  אימייל: {trainee.email || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  יעד קלורי: {trainee.caloricGoal}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  קלוריות יומיות: {trainee.dailyCalories}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  תוכניות שהועלו: {trainee.programs.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  תפריטים שהועלו: {trainee.nutritionPlans.length}
                </Typography>
              </Box>
            </Box>
            <Tooltip title="צפה בגרפים" arrow>
              <IconButton
                sx={{
                  backgroundColor: '#B5A78A',
                  color: 'white',
                  '&:hover': { backgroundColor: '#B5A78A' },
                }}
                onClick={() => handleOpenChart(trainee)}
              >
                <InsertChart />
              </IconButton>
            </Tooltip>
          </Box>
        ))
      )}

      {selectedTrainee && (
        <Dialog open={chartOpen} onClose={() => setChartOpen(false)} fullWidth maxWidth="md">
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            centered={false}
            TabIndicatorProps={{ style: { backgroundColor: '#C4A484' } }}
          >
            <Tab
              label="התקדמות משקל"
              sx={{ color: tabIndex === 0 ? '#C4A484' : 'inherit', '&.Mui-selected': { color: '#C4A484' } }}
            />
            <Tab
              label="חלוקת מזון"
              sx={{ color: tabIndex === 1 ? '#C4A484' : 'inherit', '&.Mui-selected': { color: '#C4A484' } }}
            />
            <Tab
              label="ארוחות"
              sx={{ color: tabIndex === 2 ? '#C4A484' : 'inherit', '&.Mui-selected': { color: '#C4A484' } }}
            />
            <Tab
              label="מעקב קלוריות"
              sx={{ color: tabIndex === 3 ? '#C4A484' : 'inherit', '&.Mui-selected': { color: '#C4A484' } }}
            />
            <Tab
              label="תפריטים"
              sx={{ color: tabIndex === 4 ? '#C4A484' : 'inherit', '&.Mui-selected': { color: '#C4A484' } }}
            />
            <Tab
              label="תוכניות"
              sx={{ color: tabIndex === 5 ? '#C4A484' : 'inherit', '&.Mui-selected': { color: '#C4A484' } }}
            />
          </Tabs>
          <Box p={2}>
            {tabIndex === 0 && (
              <Line
                data={{
                  labels: selectedTrainee.weightProgress.map((wp) => new Date(wp.date).toLocaleDateString()),
                  datasets: [
                    {
                      label: 'משקל (ק"ג)',
                      data: selectedTrainee.weightProgress.map((wp) => wp.weight),
                      borderColor: '#C4A484',
                      backgroundColor: 'rgba(196, 164, 132, 0.2)',
                      fill: true,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' } },
                  scales: { y: { beginAtZero: false } },
                }}
              />
            )}
            {tabIndex === 1 && (
              Object.keys(selectedTrainee.mealTypeCalories).length > 0 ? (
                <Pie
                  data={{
                    labels: Object.keys(selectedTrainee.mealTypeCalories).map((key) => mealTypes[key] || key),
                    datasets: [
                      {
                        data: Object.values(selectedTrainee.mealTypeCalories),
                        backgroundColor: ['#A3C9A8', '#EDE0D4', '#C4A484', '#B5A78A'],
                      },
                    ],
                  }}
                  options={{ responsive: true, plugins: { legend: { position: 'top' } } }}
                />
              ) : (
                <Typography sx={{ direction: 'rtl', textAlign: 'right' }}>
                  אין נתונים להצגה.
                </Typography>
              )
            )}
            {tabIndex === 2 && renderTraineeMealDetails()}
            {tabIndex === 3 && (
              <Box>
                {calorieChartData ? (
                  <Line
                    data={calorieChartData}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: 'top' } },
                      scales: { x: { display: true }, y: {} },
                    }}
                  />
                ) : (
                  <Typography sx={{ direction: 'rtl', textAlign: 'right' }}>
                    אין נתונים להצגה.
                  </Typography>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginTop: '16px' }}>
                  {['daily', 'weekly', 'monthly', 'yearly'].map((period) => (
                    <span
                      key={period}
                      onClick={() => setTimePeriod(period)}
                      style={timePeriod === period ? activeStyle : inactiveStyle}
                    >
                      {period === 'daily' ? 'יומי' : period === 'weekly' ? 'שבועי' : period === 'monthly' ? 'חודשי' : 'שנתי'}
                    </span>
                  ))}
                </div>
              </Box>
            )}
            {tabIndex === 4 && (
              <Box>
                {nutritionThumbnails.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '20px',
                    }}
                  >
                    {nutritionThumbnails.map((thumb) => {
                      const plan = selectedTrainee.nutritionPlans.find((plan) => plan._id === thumb.id);
                      return (
                        <Box
                          key={thumb.id}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            textAlign: 'center',
                            padding: '10px',
                            backgroundColor: '#fff',
                            borderRadius: '5px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                          onClick={() => openPdfModal(plan.fileBlob)}
                        >
                          <img
                            src={thumb.thumbnailUrl}
                            alt="תפריט"
                            style={{ width: '100%', height: 'auto' }}
                          />
                          <Typography noWrap sx={{ mt: 1 }}>
                            {plan.fileName}
                          </Typography>
                        </Box>
                      );
                    })}
                  </div>
                ) : (
                  <Typography direction="rtl" textAlign="right">
                    אין תפריטים להצגה.
                  </Typography>
                )}
              </Box>
            )}
            {tabIndex === 5 && (
              <Box>
                {programThumbnails.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '20px',
                    }}
                  >
                    {programThumbnails.map((thumb) => (
                      <div
                        key={thumb.id}
                        onClick={() => openPdfModal(selectedTrainee.programs.find(program => program._id === thumb.id).fileBlob)}
                        style={{
                          cursor: 'pointer',
                          textAlign: 'center',
                          marginBottom: '20px',
                        }}
                      >
                        <img
                          src={thumb.thumbnailUrl}
                          alt="תוכנית"
                          style={{ width: '100%', height: 'auto' }}
                        />
                        <p>{selectedTrainee.programs.find(program => program._id === thumb.id).fileName}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Typography direction="rtl" textAlign="right">אין תוכניות להצגה.</Typography>
                )}
              </Box>
            )}
          </Box>
        </Dialog>
      )}

      {/* Modal for viewing PDFs */}
      <Modal
        isOpen={pdfModalOpen}
        onRequestClose={closePdfModal}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1500,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            border: "none",
            borderRadius: "8px",
            width: "80%",
            height: "80%",
            maxWidth: "1200px",
            maxHeight: "90vh",
            padding: "0",
            margin: "0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
          },
        }}
      >
        <button
          onClick={closePdfModal}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "none",
            border: "none",
            color: "#000",
            fontSize: "30px",
            cursor: "pointer",
          }}
        >
          ×
        </button>
        <IconButton
          onClick={handleDownload}
          style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 2 }}
        >
          <FileDownloadIcon />
        </IconButton>
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
            display: "flex",
            gap: "10px",
          }}
        >
          <span
            onClick={() => setPdfScale((prev) => Math.min(prev + 0.1, 3))}
            style={{
              fontSize: "24px",
              cursor: "pointer",
              color: "#000",
              userSelect: "none",
            }}
          >
            +
          </span>
          <span
            onClick={() => setPdfScale((prev) => Math.max(prev - 0.1, 0.5))}
            style={{
              fontSize: "24px",
              cursor: "pointer",
              color: "#000",
              userSelect: "none",
            }}
          >
            -
          </span>
        </div>
        <div
          id="pdf-container"
          style={{ width: "100%", height: "100%", overflow: "auto" }}
        ></div>
      </Modal>
    </Box>
  );
};

export default Portal;